import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ALL_LOCATIONS, getSelectedLocation } from './LocationSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { STUDIO_SCOPED_ROLES } from '../utils/roles';

interface AdminGuardProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requireSuperAdmin?: boolean;
  /**
   * Studio-role users (studio / manager / instructor) are always redirected
   * away from this route — it is an admin-only tool (e.g. the /studios
   * directory list).
   */
  blockStudioRoles?: boolean;
  /**
   * Name of the route param holding a studio id. Studio-role users may only
   * open this route when that id is THEIR OWN studio — any other id (URL
   * tampering) redirects to /dashboard. Admin / super_admin are unrestricted.
   * Used by /studios/:studioId, which doubles as the studio owner's own
   * "Studio Profile" page.
   */
  restrictStudioRoleToOwnParam?: string;
}

const STUDIO_ROLES = STUDIO_SCOPED_ROLES;

const AdminGuard: React.FC<AdminGuardProps> = ({
  children,
  requiredPermission,
  requireSuperAdmin = false,
  blockStudioRoles = false,
  restrictStudioRoleToOwnParam,
}) => {
  const { isAuthenticated, isLoading, hasPermission, admin } = useAuth();
  const location = useLocation();
  const params = useParams();

  const isStudioRole = STUDIO_ROLES.includes(admin?.role || '');

  const redirectUnauthorized = () => {
    if (isStudioRole) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/unauthorized" replace />;
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireSuperAdmin && admin?.role !== 'super_admin') {
    return redirectUnauthorized();
  }

  // Admin-only route — studio roles have no business here regardless of the
  // permission map (which grants them 'studios.view' for their own profile page).
  if (blockStudioRoles && isStudioRole) {
    return <Navigate to="/dashboard" replace />;
  }

  // Studio roles may only open this route for their OWN studio id. Blocks the
  // "copy another studio's id into the URL" case; admins are unrestricted.
  if (restrictStudioRoleToOwnParam && isStudioRole) {
    const ownId = admin?.studio?._id ? String(admin.studio._id) : '';
    const routeId = params[restrictStudioRoleToOwnParam];
    // The location picked in the switcher counts as "own" too: a brand owner
    // opening another location's profile selects it first (see
    // studioProfile/LocationProfiles), and right after that reload the stored
    // `admin.studio` can still be the previous one until verify-admin answers.
    // Safe to allow — every request carries that selection and the server
    // checks it against the person's grants.
    const selected = getSelectedLocation();
    const isSelected = !!routeId && selected !== ALL_LOCATIONS && routeId === selected;
    if ((!ownId || routeId !== ownId) && !isSelected) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (!requiredPermission) {
    return <>{children}</>;
  }


  if (requiredPermission && !hasPermission(requiredPermission)) {
    return redirectUnauthorized();
  }

  return <>{children}</>;
};

export default AdminGuard;
